import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useGeoCountry } from '../hooks/useGeoCountry';
import { canonicalLocalPlacement, chooseAdSource, googleCanServe, publicAdSurfaceAllowed } from '../lib/adDelivery';
import LocalAdCreative from './LocalAdCreative';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Track if ad script has been loaded globally
let globalAdScriptLoaded = false;

// Module-level config cache: every <GoogleAd /> instance shares the same
// in-flight promise so we only ever hit /api/adsense/config ONCE per session.
// This eliminates the 429 console-spam observed during multi-route navigations.
let configPromise = null;
const fetchAdsConfigOnce = () => {
  if (!configPromise) {
    configPromise = axios
      .get(`${API}/api/adsense/config`)
      .then((res) => res.data)
      .catch(() => null); // silent — ads being unavailable is non-critical
  }
  return configPromise;
};

// Hard-coded defensive list of routes that must NEVER serve AdSense.
// Mirrors backend ADSENSE_RESTRICTED_PREFIXES so even if the /adsense/config
// fetch fails (network error / stale cache), these surfaces stay ad-free.
// Rationale: Google's 2026 gambling policy classifies virtual-currency
// wagering tied to real-world value (our venue vouchers) as gambling content.
// Iter 206: AdSense forbids ads on gambling pages (/odds*) AND ads should
// never serve on the admin CMS (creator-facing chrome only, no revenue
// surface). Block ANY /admin path including the dashboard, creator settings,
// social calendar, etc.
const ADSENSE_RESTRICTED_PREFIXES = ['/odds', '/admin'];

const isRestrictedPath = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  return ADSENSE_RESTRICTED_PREFIXES.some((p) => path.startsWith(p));
};

/**
 * GoogleAd Component - Renders Google AdSense ads based on admin configuration
 * 
 * Usage:
 * <GoogleAd placement="homepage_banner" />
 * <GoogleAd placement="sidebar" />
 * <GoogleAd placement="between_content" />
 */
export const GoogleAdUnit = ({ placement, className = '' }) => {
  const [config, setConfig] = useState(null);
  const [adLoaded, setAdLoaded] = useState(globalAdScriptLoaded);
  const configFetched = useRef(false);
  const insRef = useRef(null);
  const pushedRef = useRef(false);

  // Fetch config and load script in single effect
  useEffect(() => {
    if (configFetched.current) return;
    configFetched.current = true;
    // Restricted routes: skip config fetch entirely
    if (isRestrictedPath()) return;
    
    const initialize = async () => {
      const configData = await fetchAdsConfigOnce();
      if (!configData) return;
      setConfig(configData);

      // Load ad script if enabled
      if (configData?.enabled && configData?.publisher_id && !globalAdScriptLoaded) {
        if (!document.querySelector('script[src*="adsbygoogle"]')) {
          const script = document.createElement('script');
          script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${configData.publisher_id}`;
          script.async = true;
          script.crossOrigin = 'anonymous';
          script.onload = () => {
            globalAdScriptLoaded = true;
            setAdLoaded(true);
          };
          document.head.appendChild(script);
        } else {
          globalAdScriptLoaded = true;
          setAdLoaded(true);
        }
      }
    };
    
    initialize();
  }, []);

  // Iter 204: trigger the per-<ins> push() programmatically once both the
  // adsbygoogle script AND our config are ready. The previous version
  // rendered an inline <script dangerouslySetInnerHTML=...> which React 18
  // does NOT reliably re-execute on SPA route changes — so ads worked on
  // the first page only (whichever loaded the script) and never rendered
  // on subsequent client-side navigations. Calling push() from useEffect
  // is Google's officially recommended pattern for SPAs.
  useEffect(() => {
    if (!adLoaded || !config?.enabled || !config?.publisher_id) return;
    if (pushedRef.current) return;
    if (!insRef.current) return;
    // Skip the push if THIS <ins> has already been initialized by adsbygoogle
    // (Google sets data-adsbygoogle-status on processed elements).
    if (insRef.current.getAttribute('data-adsbygoogle-status')) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
      // Iter 205: tell the backend this placement actually rendered on
      // this page so admins can see in /admin/ads which pages are
      // generating impressions vs which are dark. Fire-and-forget — never
      // block ad rendering on this telemetry call.
      try {
        const path = typeof window !== 'undefined'
          ? window.location.pathname : '/';
        axios.post(`${API}/api/ads/seen`,
          { placement, page_path: path }, { timeout: 4000 }).catch(() => {});
      } catch {
        // Silent — telemetry must never break the page
      }
    } catch (e) {
      // Push throws "TagError: All adsbygoogle slots already filled"
      // when the same <ins> is pushed twice. Safe to ignore.
    }
  }, [adLoaded, config?.enabled, config?.publisher_id, placement]);

  // Don't render on AdSense-restricted routes (defensive, in addition to backend exclusion)
  if (isRestrictedPath()) {
    return null;
  }

  // Don't render if ads are disabled
  if (!config?.enabled) {
    return null;
  }

  // Check if current page is excluded
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    const excludedPages = config.excluded_pages || [];
    if (excludedPages.some(page => currentPath.startsWith(page))) {
      return null;
    }
  }

  // Resolution priority for which slot to render:
  //   1. Explicit per-placement slot ID  (admin enabled + ad_slot set)
  //   2. Wildcard default_ad_slot         (bulk fill)
  //   3. Auto-ads placeholder             (Google handles placement)
  //   4. Nothing
  // Iter 199: also resolves per-placement FORMAT so the <ins> tag uses the
  // right AdSense recipe (banner/sidebar/in-article/in-feed etc.). When
  // auto-ads is ON the manual <ins> tags still render — Google's algorithm
  // sees them and avoids placing auto-ads on top of them.
  const placementConfig = config.ad_placements?.[placement];
  const explicitSlot = placementConfig?.enabled ? placementConfig?.ad_slot : '';
  const defaultSlot = config.default_ad_slot || '';
  const adSlot = explicitSlot || defaultSlot;
  const adFormat = (placementConfig?.enabled && placementConfig?.ad_format)
    || config.default_ad_format
    || 'display';
  const adLayoutKey = (placementConfig?.enabled && placementConfig?.ad_layout_key) || '';

  // Nothing configured at all for this placement → don't render
  if (!adSlot && !config.auto_ads) {
    return null;
  }

  // Map our internal format name to the actual <ins> attributes Google
  // expects. Each branch returns the data-* prop bag.
  const buildInsProps = (slot, format, layoutKey) => {
    const base = {
      'data-ad-client': config.publisher_id,
      'data-ad-slot': slot,
      'data-testid': `adsense-ins-${placement}`,
    };
    switch (format) {
      case 'horizontal':
        return { ...base, 'data-ad-format': 'horizontal',
                 'data-full-width-responsive': 'true' };
      case 'vertical':
        return { ...base, 'data-ad-format': 'vertical' };
      case 'rectangle':
        return { ...base, 'data-ad-format': 'rectangle' };
      case 'in_article':
        return { ...base, 'data-ad-format': 'fluid',
                 'data-ad-layout': 'in-article' };
      case 'in_feed':
        return { ...base, 'data-ad-format': 'fluid',
                 ...(layoutKey ? { 'data-ad-layout-key': layoutKey } : {}) };
      case 'display':
      default:
        return { ...base, 'data-ad-format': 'auto',
                 'data-full-width-responsive': 'true' };
    }
  };

  return (
    <div
      className={`google-ad-container ${className}`}
      data-testid={`google-ad-${placement}`}
      data-ad-format={adFormat}
    >
      {adSlot ? (
        // Explicit slot (per-placement OR wildcard default) — preferred path
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          {...buildInsProps(adSlot, adFormat, adLayoutKey)}
        />
      ) : (
        // Auto-ads fallback — Google decides where to put it
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={config.publisher_id}
          data-ad-slot="auto"
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      )}
    </div>
  );
};

/**
 * One delivery contract for every public ad slot.
 * Paid local campaigns lead each session rotation. Google participates only
 * when it is configured; house ads are used only when neither paid source is
 * available.
 */
const UnifiedAdSlot = ({ placement, className = '', variant = 'banner', title = '' }) => {
  const geo = useGeoCountry();
  const [delivery, setDelivery] = useState({ loading: true, source: null, ad: null });
  const localPlacement = canonicalLocalPlacement(placement);

  useEffect(() => {
    let active = true;
    if (geo.loading) return undefined;
    if (typeof window !== 'undefined' && !publicAdSurfaceAllowed(window.location.pathname)) {
      setDelivery({ loading: false, source: null, ad: null });
      return undefined;
    }

    Promise.all([
      axios.get(`${API}/api/ads/${localPlacement}`, {
        params: { country: geo.country_code },
      }).then((response) => response.data).catch(() => ({ ad: null })),
      fetchAdsConfigOnce(),
    ]).then(([localResponse, googleConfig]) => {
      if (!active) return;
      if (localResponse?.reason === 'gold_member') {
        setDelivery({ loading: false, source: null, ad: null });
        return;
      }
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const source = chooseAdSource({
        ad: localResponse?.ad,
        isDefault: Boolean(localResponse?.is_default),
        googleAvailable: googleCanServe(googleConfig, placement, pathname),
        placement: localPlacement,
      });
      setDelivery({ loading: false, source, ad: localResponse?.ad || null });
    });

    return () => { active = false; };
  }, [geo.country_code, geo.loading, localPlacement, placement]);

  if (delivery.loading || !delivery.source) return null;
  if (delivery.source === 'google') {
    return (
      <div className={variant === 'banner' ? 'my-6' : ''} data-ad-source="google">
        {title && <p className="mb-2 text-center text-xs text-muted-foreground">{title}</p>}
        <GoogleAdUnit placement={placement} className={className} />
      </div>
    );
  }
  return <LocalAdCreative ad={delivery.ad} variant={variant} className={className} />;
};

/**
 * AdBanner - Pre-styled ad banner for specific locations
 */
export const AdBanner = ({ placement, title = 'Advertisement' }) => {
  return <UnifiedAdSlot placement={placement} className="w-full" variant="banner" title={title} />;
};

/**
 * SidebarAd - For sidebar placements
 */
export const SidebarAd = () => {
  return (
    <div className="glass-card rounded-sm p-4">
      <p className="text-xs text-muted-foreground mb-2">Sponsored</p>
      <UnifiedAdSlot placement="sidebar" variant="sidebar" />
    </div>
  );
};

/**
 * InArticleAd - For inline article placements
 */
export const InArticleAd = () => {
  return (
    <div className="my-8 py-4 border-y border-white/10">
      <UnifiedAdSlot placement="article_inline" variant="banner" />
    </div>
  );
};

export default UnifiedAdSlot;
